import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, FileText, CheckCircle, AlertCircle, Download, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CSVUtils, ValidationError, ImportResult } from '../../utils/csvUtils';
import { dataService } from '../../services/dataService';

interface CSVImportModalProps {
  type: 'rawMaterial' | 'menuItem';
  onClose: () => void;
  onSuccess: () => void;
}

type ImportMode = 'create' | 'update' | 'createAndUpdate';

const CSVImportModal: React.FC<CSVImportModalProps> = ({ type, onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importMode, setImportMode] = useState<ImportMode>('createAndUpdate');
  const [showPreview, setShowPreview] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    // Validate file size (5MB limit)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setError('');
    setValidationErrors([]);
    setImportResult(null);

    try {
      // Parse CSV
      const { data, errors } = await CSVUtils.parseCSV(selectedFile);
      
      if (errors.length > 0) {
        setError('Error parsing CSV file. Please check the format.');
        console.error('CSV parsing errors:', errors);
        return;
      }

      if (data.length === 0) {
        setError('No data found in CSV file');
        return;
      }

      if (data.length > 1000) {
        setError('CSV file contains too many rows. Maximum 1000 rows allowed.');
        return;
      }

      setParsedData(data);

      // Validate data
      const allErrors: ValidationError[] = [];
      data.forEach((row, index) => {
        const errors = type === 'rawMaterial' 
          ? CSVUtils.validateRawMaterial(row, index)
          : CSVUtils.validateMenuItem(row, index);
        allErrors.push(...errors);
      });

      setValidationErrors(allErrors);
      setShowPreview(allErrors.length === 0);

    } catch (err) {
      console.error('Error processing file:', err);
      setError('Error processing file. Please try again.');
    }
  }, [type]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleImport = async () => {
    if (!parsedData.length || validationErrors.length > 0) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let result: ImportResult;

      if (type === 'rawMaterial') {
        const materials = parsedData.map(CSVUtils.csvToRawMaterial);
        // Use CSV sync for raw materials to handle stock changes with transactions
        result = await dataService.csvSyncRawMaterials(tenantData!.id, materials, 'CSV Import');
      } else {
        const items = parsedData.map(CSVUtils.csvToMenuItem);
        result = await dataService.bulkCreateMenuItems(tenantData!.id, items, importMode);
      }

      setImportResult(result);
      
      if (result.success) {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }

    } catch (err) {
      console.error('Import error:', err);
      setError('Failed to import data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadErrorReport = () => {
    const errorReport = CSVUtils.generateErrorReport(validationErrors);
    CSVUtils.downloadCSV(errorReport, `import-errors-${type}-${Date.now()}.csv`);
  };

  const downloadTemplate = () => {
    const template = CSVUtils.generateTemplate(type);
    CSVUtils.downloadCSV(template, `${type}-template.csv`);
  };

  const canImport = parsedData.length > 0 && validationErrors.length === 0 && !loading;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
              Import {type === 'rawMaterial' ? 'Raw Materials' : 'Menu Items'} from CSV
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Upload a CSV file to import data in bulk
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Template Download */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Download Template
              </h3>
              <button
                onClick={downloadTemplate}
                className="btn-secondary text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Download the CSV template with the correct format and example data.
            </p>
          </div>

          {/* File Upload */}
          {!file && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Upload CSV File
              </h3>
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors cursor-pointer"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Maximum file size: 5MB | Maximum rows: 1000
                </p>
                <input
                  id="file-input"
                  type="file"
                  accept=".csv"
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* File Selected */}
          {file && !importResult && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  File Selected
                </h3>
                <button
                  onClick={() => {
                    setFile(null);
                    setParsedData([]);
                    setValidationErrors([]);
                    setShowPreview(false);
                    setError('');
                  }}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Remove File
                </button>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 flex items-center space-x-3">
                <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {(file.size / 1024).toFixed(1)} KB • {parsedData.length} rows
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Validation Errors ({validationErrors.length})
                </h3>
                <button
                  onClick={downloadErrorReport}
                  className="btn-secondary text-sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Error Report
                </button>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 max-h-48 overflow-y-auto">
                {validationErrors.slice(0, 10).map((error, index) => (
                  <div key={index} className="mb-2 last:mb-0">
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <span className="font-medium">Row {error.row}, {error.field}:</span> {error.message}
                      {error.value && <span className="text-red-600 dark:text-red-400"> (Value: {error.value})</span>}
                    </p>
                  </div>
                ))}
                {validationErrors.length > 10 && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    ... and {validationErrors.length - 10} more errors
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Import Mode Selection */}
          {parsedData.length > 0 && validationErrors.length === 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Import Behavior
              </h3>
              {type === 'rawMaterial' ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span className="font-medium text-blue-800 dark:text-blue-200">CSV Sync Mode</span>
                  </div>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Raw materials will be matched by SKU (if provided) or name. Stock changes will be tracked as adjustment transactions.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="importMode"
                      value="create"
                      checked={importMode === 'create'}
                      onChange={(e) => setImportMode(e.target.value as ImportMode)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Create only</span> - Skip items that already exist (by name)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="importMode"
                      value="update"
                      checked={importMode === 'update'}
                      onChange={(e) => setImportMode(e.target.value as ImportMode)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Update existing</span> - Only update items that already exist (by name)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="importMode"
                      value="createAndUpdate"
                      checked={importMode === 'createAndUpdate'}
                      onChange={(e) => setImportMode(e.target.value as ImportMode)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                    />
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Create and update</span> - Create new items and update existing ones
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Data Preview */}
          {parsedData.length > 0 && validationErrors.length === 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Data Preview
                </h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="btn-secondary text-sm"
                >
                  {showPreview ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      Hide Preview
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Show Preview
                    </>
                  )}
                </button>
              </div>
              {showPreview && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-64">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          {Object.keys(parsedData[0] || {}).slice(0, 8).map((key) => (
                            <th key={key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {parsedData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.keys(row).slice(0, 8).map((key) => (
                              <td key={key} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                {row[key] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {parsedData.length > 5 && (
                    <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      ... and {parsedData.length - 5} more rows
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="mb-6">
              <div className={`rounded-lg p-4 ${
                importResult.success 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <div className="flex items-center mb-3">
                  {importResult.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mr-2" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 mr-2" />
                  )}
                  <h3 className={`text-lg font-semibold ${
                    importResult.success 
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                    Import {importResult.success ? 'Successful' : 'Failed'}
                  </h3>
                </div>
                <div className="text-sm">
                  <p className={importResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                    Processed {importResult.totalRows} rows • Created {importResult.created} • Updated {importResult.updated}
                  </p>
                  {importResult.errors.length > 0 && (
                    <p className="text-red-600 dark:text-red-400 mt-2">
                      {importResult.errors.length} errors occurred during import
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            {canImport && !importResult && (
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Importing...' : `Import ${parsedData.length} ${type === 'rawMaterial' ? 'Materials' : 'Items'}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CSVImportModal;
